import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Fish, PiggyBank, Leaf, TreePalm, Carrot } from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  {
    id: "pig-farming",
    icon: PiggyBank,
    title: "Pig Farming",
    subtitle: "Premium Livestock",
    description: "Our pig farming operation raises healthy, well-fed pigs using modern husbandry techniques. We focus on proper nutrition, veterinary care, and comfortable housing to produce quality pork.",
    features: [
      "Breeds: Large White, Duroc, Hampshire",
      "Proper vaccination and health monitoring",
      "Quality feed formulation",
      "Clean and spacious housing",
      "Available for breeding stock or meat"
    ],
    image: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/54tu1wv0_pig-photo111.jpg",
    color: "green"
  },
  {
    id: "fish-farming",
    icon: Fish,
    title: "Fish Farming (Aquaculture)",
    subtitle: "Fresh Seafood",
    description: "Our aquaculture ponds produce fresh catfish. We maintain optimal water quality and use quality fish feed to ensure healthy, fast-growing fish.",
    features: [
      "Species: Catfish",
      "Regular water quality monitoring",
      "High-protein fish feed",
      "Both fingerlings and table-size available",
      "Wholesale and retail quantities"
    ],
    image: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/wnfypbzf_WhatsApp%20Image%202026-04-01%20at%2010.59.50%20AM.jpeg",
    color: "terracotta"
  },
  {
    id: "grasscutter",
    icon: Leaf,
    title: "Grasscutter Farming",
    subtitle: "Nigerian Delicacy",
    description: "We breed and raise grasscutters (cane rats), a highly sought-after delicacy in Nigeria. Our grasscutters are raised in a controlled environment for optimal growth.",
    features: [
      "High-demand meat product",
      "Controlled breeding program",
      "Proper diet of grass and supplements",
      "Available live or processed",
      "Breeding stock available"
    ],
    image: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/54lc9buf_image.png",
    color: "gold"
  },
  {
    id: "vegetables",
    icon: Carrot,
    title: "Vegetable Production",
    subtitle: "Fresh Produce",
    description: "Our vegetable section produces fresh, organic vegetables including tomatoes, peppers, leafy greens, and more. We use sustainable farming methods to grow nutrient-rich produce.",
    features: [
      "Tomatoes, peppers, leafy greens",
      "Organic farming methods",
      "Daily fresh harvest",
      "Direct farm-to-table supply",
      "Bulk orders welcome"
    ],
    image: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/2mv30pjo_The-Secret-History-of-Okra-1.jpg",
    color: "green"
  },
  {
    id: "palm",
    icon: TreePalm,
    title: "Palm Trees & Crop Cultivation",
    subtitle: "Palm Products",
    description: "Our palm plantation produces quality palm fruits for palm oil extraction. We also cultivate various crops to complement our agricultural offerings.",
    features: [
      "Palm oil production",
      "Palm kernel processing",
      "Sustainable palm cultivation",
      "Quality assured products",
      "Wholesale quantities available"
    ],
    image: "https://images.unsplash.com/photo-1671208615878-c074b4bf08dc?w=800",
    color: "terracotta"
  }
];

const colorClasses = {
  green: {
    bg: "bg-green-100",
    text: "text-green-600",
    border: "border-green-200"
  },
  terracotta: {
    bg: "bg-orange-100",
    text: "text-terracotta",
    border: "border-orange-200"
  },
  gold: {
    bg: "bg-amber-100",
    text: "text-amber-600",
    border: "border-amber-200"
  }
};

export default function ServicesPage() {
  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="section-padding bg-stone-900 text-white" data-testid="services-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="font-accent text-sm uppercase tracking-widest text-gold mb-6 block">
              Our Services
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              Comprehensive Agricultural Services
            </h1>
            <p className="text-stone-300 text-lg md:text-xl leading-relaxed">
              From livestock to crops, AfriGrow Farm offers a diverse range of quality agricultural 
              products and services tailored to meet your needs.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding bg-stone-50" data-testid="services-list">
        <div className="container-custom space-y-24">
          {services.map((service, index) => (
            <div 
              key={service.id}
              className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              data-testid={`service-detail-${service.id}`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="relative rounded-2xl overflow-hidden shadow-xl img-zoom">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-[400px] object-cover"
                  />
                  <div className={`absolute top-6 left-6 ${colorClasses[service.color].bg} backdrop-blur-sm px-4 py-2 rounded-full`}>
                    <span className={`font-accent text-sm font-medium ${colorClasses[service.color].text}`}>
                      {service.subtitle}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`p-4 ${colorClasses[service.color].bg} rounded-xl`}>
                    <service.icon className={`h-8 w-8 ${colorClasses[service.color].text}`} />
                  </div>
                  <h2 className="font-heading text-3xl md:text-4xl font-semibold text-stone-900">
                    {service.title}
                  </h2>
                </div>
                
                <p className="text-stone-600 text-lg leading-relaxed mb-8">
                  {service.description}
                </p>
                
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className={`h-6 w-6 ${colorClasses[service.color].text} flex-shrink-0 mt-0.5`} />
                      <span className="text-stone-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  asChild
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6 py-5"
                  data-testid={`inquiry-btn-${service.id}`}
                >
                  <Link to="/contact">
                    Inquire Now <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-white" data-testid="process-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="font-accent text-sm uppercase tracking-widest text-terracotta mb-4 block">
              How It Works
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-stone-900">
              Our Process
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Contact Us", desc: "Reach out via phone or our contact form" },
              { step: "02", title: "Discuss Needs", desc: "Tell us what products you need" },
              { step: "03", title: "Get Quote", desc: "Receive a competitive price quote" },
              { step: "04", title: "Delivery", desc: "We deliver fresh to your location" }
            ].map((item, index) => (
              <div key={index} className="text-center" data-testid={`process-step-${index}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 text-white rounded-full font-heading text-2xl font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading text-xl font-semibold text-stone-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-stone-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-green-600" data-testid="services-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Need a Custom Service?
          </h2>
          <p className="text-green-100 text-lg max-w-2xl mx-auto mb-10">
            We can accommodate special requests and bulk orders. Contact us to discuss your specific needs.
          </p>
          <Button 
            asChild 
            size="lg"
            className="bg-white text-green-700 hover:bg-stone-100 rounded-full px-8 py-6 text-lg font-semibold"
            data-testid="custom-service-btn"
          >
            <Link to="/contact">
              Request Custom Service <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
